import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { encryptSecret } from '../services/cryptoService';
import { generateTOTP } from '../services/totpService';
import QRScanner from '../components/QRScanner';

interface TotpEntry {
  id: string;
  name: string;
  encryptedSecret: string;
  iconUrl?: string;
  category?: string;
  color?: string;
  deviceTag?: string;
  updatedAt: number;
}

const TotpPage: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [entries, setEntries] = useState<TotpEntry[]>([]);
  const [name, setName] = useState('');
  const [secret, setSecret] = useState('');
  const [category, setCategory] = useState('');
  const [color, setColor] = useState('#ffffff');
  const [backupPassword, setBackupPassword] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [totpCodes, setTotpCodes] = useState<{[key: string]: string}>({});

  const fetchEntries = useCallback(async () => {
    const res = await fetch('/api/totp', {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    setEntries(data);
  }, [token]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Update TOTP codes every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (backupPassword && entries.length > 0) {
        const newCodes: {[key: string]: string} = {};
        entries.forEach(entry => {
          newCodes[entry.id] = generateTOTP(entry.encryptedSecret, backupPassword);
        });
        setTotpCodes(newCodes);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [backupPassword, entries]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!backupPassword) {
      setError(t('backup_password_required'));
      return;
    }
    try {
      const encryptedSecret = encryptSecret(secret, backupPassword);
      const res = await fetch('/api/totp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name,
          encryptedSecret,
          category: category || undefined,
          color,
          updatedAt: Date.now(),
        }),
      });
      if (!res.ok) throw new Error(t('error_adding_totp'));
      resetForm();
      fetchEntries();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId || !backupPassword) return;
    try {
      const encryptedSecret = encryptSecret(secret, backupPassword);
      const res = await fetch(`/api/totp/${editingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name,
          encryptedSecret,
          category: category || undefined,
          color,
          updatedAt: Date.now(),
        }),
      });
      if (!res.ok) throw new Error(t('error_updating_totp'));
      resetForm();
      fetchEntries();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (id: string) => {
    await fetch(`/api/totp/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchEntries();
  };

  const handleEdit = (entry: TotpEntry) => {
    setEditingId(entry.id);
    setName(entry.name);
    setSecret(''); // Don't show the secret
    setCategory(entry.category || '');
    setColor(entry.color || '#ffffff');
    setShowAddForm(true);
  };

  const handleQRScan = (scannedSecret: string, scannedName: string) => {
    setSecret(scannedSecret);
    setName(scannedName);
    setShowAddForm(true);
  };

  const resetForm = () => {
    setName(''); 
    setSecret(''); 
    setCategory(''); 
    setColor('#ffffff');
    setEditingId(null);
    setShowAddForm(false);
  };

  return (
    <div style={{ maxWidth: 800, margin: 'auto', padding: 32 }}>
      <h2>{t('totp_codes')}</h2>
      
      <div style={{ marginBottom: 16 }}>
        <input
          type="password"
          placeholder={t('enter_backup_password')}
          value={backupPassword}
          onChange={e => setBackupPassword(e.target.value)}
          style={{ marginRight: 8 }}
        />
        <button onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? t('cancel') : t('add_code')}
        </button>
        <button onClick={() => setShowQRScanner(true)} style={{ marginLeft: 8 }}>
          {t('scan_qr')}
        </button>
      </div>
      
      {showAddForm && (
        <form onSubmit={editingId ? handleUpdate : handleAdd} style={{ marginBottom: 24, padding: 16, border: '1px solid #ccc' }}>
          <div>
            <label htmlFor="totp_name">{t('name')}:</label>
            <input id="totp_name" value={name} onChange={e => setName(e.target.value)} required />
          </div>
          <div>
            <label htmlFor="totp_secret">{t('secret')}:</label>
            <input id="totp_secret" value={secret} onChange={e => setSecret(e.target.value)} required />
          </div>
          <div>
            <label htmlFor="totp_category">{t('category')}:</label>
            <input id="totp_category" value={category} onChange={e => setCategory(e.target.value)} />
          </div>
          <div>
            <label htmlFor="totp_color">{t('color')}:</label>
            <input id="totp_color" type="color" value={color} onChange={e => setColor(e.target.value)} />
          </div>
          <div>
            <label htmlFor="totp_backup_password">{t('backup_password')}:</label>
            <input
              id="totp_backup_password"
              type="password"
              value={backupPassword}
              onChange={e => setBackupPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit">{editingId ? t('update') : t('add')}</button>
          {editingId && <button type="button" onClick={resetForm} style={{ marginLeft: 8 }}>{t('cancel')}</button>}
          {error && <span style={{ color: 'red', marginLeft: 8 }} role="alert">{error}</span>}
        </form>
      )}

      <div style={{ display: 'grid', gap: 16 }}>
        {entries.map(e => (
          <div key={e.id} style={{ padding: 16, border: '1px solid #ccc', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3>{e.name}</h3>
                {e.category && <span style={{ color: e.color || '#000' }}>{e.category}</span>}
                {backupPassword && totpCodes[e.id] && (
                  <div style={{ fontSize: '1.2em', fontWeight: 'bold', marginTop: 8 }}>
                    {totpCodes[e.id]}
                  </div>
                )}
              </div>
              <div>
                <button onClick={() => handleEdit(e)} style={{ marginRight: 8 }}>{t('edit')}</button>
                <button onClick={() => handleDelete(e.id)}>{t('delete')}</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showQRScanner && (
        <QRScanner
          onScan={handleQRScan}
          onClose={() => setShowQRScanner(false)}
        />
      )}
    </div>
  );
};

export default TotpPage; 